/**
 * Logging types — the shared vocabulary for the whole subsystem.
 *
 * The wire shape here is not ours to invent. It is fixed by
 * `home-ceo-backend/docs/backend_logging_contract.md` and by the dashboard
 * views that read `the-home-ceo.server_logs`, which key on flat
 * `jsonPayload.<field>` columns. Two consequences worth stating plainly:
 *
 * 1. **The record is flat.** No nested `page: { path }`. Dashboards select
 *    `jsonPayload.path`, so `path` lives at the top level. Nesting would be
 *    tidier to read and would silently break every view.
 * 2. **Wire fields are `snake_case`, code-facing APIs are `camelCase`.** The
 *    translation happens once, in `pipeline/dispatcher.ts`, so no caller in
 *    `src/` has to think about it.
 *
 * @module logging/core/types
 */

// =============================================================================
// SEVERITY
// =============================================================================

/**
 * Cloud Logging severity, spelled the way the backend's structured logger
 * spells it so the ingest endpoint can pass the value straight through.
 *
 * Only three of Cloud Logging's levels are reachable from the browser:
 * DEBUG never leaves the device, and CRITICAL is reserved for the backend
 * deciding something is page-worthy.
 */
export type Severity = 'INFO' | 'WARNING' | 'ERROR';

// =============================================================================
// PAYLOAD VALUES
// =============================================================================

/**
 * Values allowed on the wire.
 *
 * Deliberately not `unknown`: every field becomes a typed BigQuery column,
 * and an object landing in one is how a schema starts drifting. Anything
 * richer than this is a modelling mistake to resolve at the call site.
 */
export type ParamValue = string | number | boolean;

/** A validated, sanitized, flat event payload. */
export type EventParams = Readonly<Record<string, ParamValue>>;

/** Result of a timed or fallible interaction. Fixed by the dashboard contract. */
export type Outcome = 'succeeded' | 'failed' | 'cancelled';

/** Coarse error bucket. Matches the categories the dashboards already chart. */
export type ErrorCategory = 'auth' | 'network' | 'validation' | 'unknown';

// =============================================================================
// THE WIRE RECORD
// =============================================================================

/**
 * One event as it goes on the wire, and as it lands in `jsonPayload`.
 *
 * The named fields are the dashboard contract — renaming one breaks a view.
 * The index signature carries the per-event fields from the catalog, which
 * are flattened onto the same level rather than nested under `params`.
 *
 * Fields the *backend* adds on receipt, which are therefore absent here:
 * `service`, `function_name`, `timestamp`, `is_cold_start`.
 */
export interface WireEvent {
  /** Stable catalog name, `web_`-prefixed. e.g. 'web_page_viewed'. */
  event_name: string;
  /** Literal 'web'. The field that lets dashboards split by platform
   *  without maintaining a userAgent heuristic in SQL. */
  surface: 'web';
  /** Tab-scoped UUID from sessionStorage. Not an identity. */
  session_id: string;
  /** Pathname only — never a full URL, never a query string. */
  path: string;
  /** ISO-8601 UTC from the client clock, which can be wrong or spoofed.
   *  Cloud Logging stamps its own `timestamp`; prefer that for windowing
   *  and use this only for ordering within a session. */
  ts_client: string;
  /** `<package version>+<short git sha>`, injected at build time. */
  app_version: string;
  severity: Severity;
  /** Monotonic within a session, from 1. A gap is the only signal a
   *  dashboard has that events were dropped rather than never sent. */
  event_seq: number;

  /** Per-event fields from the catalog, flattened onto the record. */
  [param: string]: ParamValue;
}

// =============================================================================
// TRANSPORTS — the extension seam
// =============================================================================

/**
 * Why a flush was requested. Transports pick a delivery mechanism from it.
 *
 * `hidden` and `navigating` are the two *terminal* reasons — the page may
 * not exist a moment later, so neither can afford a retryable request.
 * They are separate values rather than one because they mean different
 * things to a reader: `hidden` is the tab being backgrounded or closed,
 * `navigating` is the visitor deliberately following a link off the site.
 * Use `isTerminalFlush` rather than comparing against either by hand.
 */
export type FlushReason =
  | 'interval'
  | 'batch_full'
  | 'hidden'
  | 'navigating'
  | 'manual';

/** Whether the page may be gone before a response could arrive. */
export function isTerminalFlush(reason: FlushReason): boolean {
  return reason === 'hidden' || reason === 'navigating';
}

/**
 * Rules every transport and sink in this subsystem obeys:
 *
 * 1. They MUST NOT throw and MUST NOT return a rejecting promise. A
 *    telemetry backend being unreachable is not a defect in the page.
 * 2. They MUST NOT write to `console.error` or `console.warn`. The e2e
 *    suite fails a route on any console error, so a noisy transport would
 *    turn a dead collector into a red build on unrelated PRs.
 * 3. They MUST NOT call back into the public logging API. They use
 *    `core/diagnostic.ts` instead — a transport reporting its own failure
 *    through the pipeline would feed itself forever.
 * 4. Delivery is best-effort. Dropping beats blocking or breaking the page.
 */

/**
 * A destination for events, as the dispatcher sees it.
 *
 * Adding one means writing an object with these two methods and listing it
 * in `transports/index.ts`. Nothing else in the subsystem changes — that is
 * the seam the whole design exists to keep clean.
 */
export interface Transport {
  /** Stable identifier, used in diagnostics. */
  readonly name: string;
  /** Take one event. Whether that means "send it now" or "buffer it" is
   *  the transport's business. */
  accept(event: WireEvent): void;
  /** Deliver anything pending. */
  flush(reason: FlushReason): void;
}

/**
 * A sink that wants events grouped, not one at a time.
 *
 * Network destinations implement this and get buffering, byte-aware
 * chunking, and flush scheduling by being wrapped in `withBatching`. Keeping
 * it separate from `Transport` means the batching policy lives in exactly
 * one place instead of being re-implemented per sink, and means a sink's
 * `send` is only ever called when a delivery is genuinely due.
 */
export interface BatchSink {
  readonly name: string;
  /**
   * Deliver a grouped batch.
   *
   * `reason` is passed because the delivery mechanism depends on it: a
   * `hidden` flush is the last moment the page is guaranteed to exist, so a
   * sink must not choose a strategy there that assumes it will be around to
   * handle a response or schedule a retry.
   */
  send(batch: readonly WireEvent[], reason: FlushReason): void;
}
